import { FishingHotspot, OceanDataSet, BoundingBox, CurrentData } from './types';
import { UnitConversions } from '../../utils/unitConversions';
import { waypointService } from '../waypointService';
import { PrivateWaypointsService } from '../privateWaypoints';

interface Structure {
  latitude: number;
  longitude: number;
  name: string;
  description: string;
}

interface GradientPoint {
  lat: number;
  lon: number;
  sstGradient: number;
  chloroGradient: number;
  currentSpeed: number;
  currentConvergence: number;
  sshAnomaly: number;
  sshGradient: number;
  eddyType: 'warm-core' | 'cold-core' | 'none';
  nearbyStructures?: Structure[];
  score: number;
  distanceFromPort?: number;
  practicalityScore?: number;
}

export class HotspotRanker {
  private static instance: HotspotRanker;
  
  private constructor() {}
  
  static getInstance(): HotspotRanker {
    if (!HotspotRanker.instance) {
      HotspotRanker.instance = new HotspotRanker();
    }
    return HotspotRanker.instance;
  }
  

  /**
   * Rank fishing hotspots based on SST gradients, chlorophyll gradients, and proximity to structures
   */
  private personalWaypointsCache: Structure[] | null = null;
  private personalWaypointsCacheUserId: string | null = null;

  async rankHotspots(
    data: OceanDataSet,
    homePort?: { lat: number; lon: number },
    options?: {
      useWaypoints?: boolean;
      waypointType?: 'public' | 'personal' | 'both';
      userId?: string;
    }
  ): Promise<FishingHotspot[]> {
    console.log('[HotspotRanker] Starting gradient-based hotspot ranking');
    console.log('[HotspotRanker] Waypoint options:', JSON.stringify(options, null, 2));
    
    const { sst, chlorophyll, currents, ssh, bounds } = data;
    const gradientPoints: GradientPoint[] = [];
    
    // First pass: Calculate all gradients to find the distribution
    const allSstGradients: number[] = [];
    const allChloroGradients: number[] = [];
    const allCurrentSpeeds: number[] = [];
    const allCurrentConvergences: number[] = [];
    const allSshGradients: number[] = [];
    const allSshAnomalies: number[] = [];
    
    for (let i = 1; i < sst.grid.length - 1; i++) {
      for (let j = 1; j < sst.grid[i].length - 1; j++) {
        const sstCenter = sst.grid[i][j];
        const chloroCenter = chlorophyll.grid[i][j];
        
        if (sstCenter === null || chloroCenter === null) continue;
        
        // Only consider areas with chlorophyll 1.2 or less
        if (chloroCenter < 0 || chloroCenter > 1.2) continue;
        
        // Calculate SST gradient (handle nulls properly)
        const sstLeft = sst.grid[i][j-1] ?? sstCenter;
        const sstRight = sst.grid[i][j+1] ?? sstCenter;
        const sstTop = sst.grid[i-1]?.[j] ?? sstCenter;
        const sstBottom = sst.grid[i+1]?.[j] ?? sstCenter;
        
        const sstDx = (sstRight - sstLeft) / 2;
        const sstDy = (sstBottom - sstTop) / 2;
        const sstGradient = Math.sqrt(sstDx * sstDx + sstDy * sstDy);
        
        // Calculate chlorophyll gradient (handle nulls properly)
        const chloroLeft = chlorophyll.grid[i][j-1] ?? chloroCenter;
        const chloroRight = chlorophyll.grid[i][j+1] ?? chloroCenter;
        const chloroTop = chlorophyll.grid[i-1]?.[j] ?? chloroCenter;
        const chloroBottom = chlorophyll.grid[i+1]?.[j] ?? chloroCenter;
        
        // Skip if any neighboring chlorophyll values are outside 0-1.2 range (likely land/invalid data)
        if (chloroLeft < 0 || chloroLeft > 1.2 || 
            chloroRight < 0 || chloroRight > 1.2 || 
            chloroTop < 0 || chloroTop > 1.2 || 
            chloroBottom < 0 || chloroBottom > 1.2) continue;
        
        const chloroDx = (chloroRight - chloroLeft) / 2;
        const chloroDy = (chloroBottom - chloroTop) / 2;
        const chloroGradient = Math.sqrt(chloroDx * chloroDx + chloroDy * chloroDy);
        
        // Skip unrealistic chlorophyll gradients (> 0.5 mg/m³/km) that indicate land/shore boundaries
        if (chloroGradient > 0.5) continue;
        
        if (sstGradient > 0) allSstGradients.push(sstGradient);
        if (chloroGradient > 0) allChloroGradients.push(chloroGradient);
        
        // Calculate current speed and convergence
        let currentSpeed = 0;
        let currentConvergence = 0;
        
        if (currents && currents.speedGrid && currents.uGrid && currents.vGrid) {
          // Get current speed at this location
          const rawSpeed = currents.speedGrid[i]?.[j];
          currentSpeed = (rawSpeed !== null && rawSpeed !== undefined) ? rawSpeed : 0;
          
          // Only log first point to avoid spam
          if (i === 1 && j === 1) {
            console.log(`[HotspotRanker] Sample current data - Speed: ${currentSpeed}, Grid size: ${currents.speedGrid.length}x${currents.speedGrid[0]?.length}`);
          }
          
          // Calculate current convergence (divergence of velocity field)
          const uCenter = currents.uGrid[i]?.[j] || 0;
          const vCenter = currents.vGrid[i]?.[j] || 0;
          
          const uLeft = currents.uGrid[i]?.[j-1] ?? uCenter;
          const uRight = currents.uGrid[i]?.[j+1] ?? uCenter;
          const vTop = currents.vGrid[i-1]?.[j] ?? vCenter;
          const vBottom = currents.vGrid[i+1]?.[j] ?? vCenter;
          
          // Convergence = -(du/dx + dv/dy)
          // Negative values indicate convergence (good for fishing)
          const dudx = (uRight - uLeft) / 2;
          const dvdy = (vBottom - vTop) / 2;
          currentConvergence = -(dudx + dvdy);
          
          if (currentSpeed > 0) allCurrentSpeeds.push(currentSpeed);
          if (Math.abs(currentConvergence) > 0) allCurrentConvergences.push(Math.abs(currentConvergence));
        } else {
          // Log missing current data
          if (i === 1 && j === 1) {
            console.log('[HotspotRanker] Current data missing or incomplete', {
              hasCurrents: !!currents,
              hasSpeedGrid: !!(currents?.speedGrid),
              hasUGrid: !!(currents?.uGrid),
              hasVGrid: !!(currents?.vGrid)
            });
          }
        }
        
        // Calculate SSH anomaly and gradient for eddy detection
        let sshAnomaly = 0;
        let sshGradient = 0;
        let eddyType: 'warm-core' | 'cold-core' | 'none' = 'none';
        
        if (ssh && ssh.grid && ssh.grid.length > 0) {
          const sshCenter = ssh.grid[i]?.[j];
          
          // Only log first point to avoid spam
          if (i === 1 && j === 1) {
            console.log(`[HotspotRanker] Sample SSH data - Value: ${sshCenter}, Grid size: ${ssh.grid.length}x${ssh.grid[0]?.length}`);
          }
          
          if (sshCenter !== null && sshCenter !== undefined) {
            // SSH anomaly (deviation from mean)
            // Positive anomaly = warm-core eddy, Negative = cold-core eddy
            sshAnomaly = sshCenter;
            
            // Calculate SSH gradient
            const sshLeft = ssh.grid[i][j-1] ?? sshCenter;
            const sshRight = ssh.grid[i][j+1] ?? sshCenter;
            const sshTop = ssh.grid[i-1]?.[j] ?? sshCenter;
            const sshBottom = ssh.grid[i+1]?.[j] ?? sshCenter;
            
            const sshDx = (sshRight - sshLeft) / 2;
            const sshDy = (sshBottom - sshTop) / 2;
            sshGradient = Math.sqrt(sshDx * sshDx + sshDy * sshDy);
            
            // Identify eddy type based on SSH anomaly and temperature
            if (Math.abs(sshAnomaly) > 0.05) { // 5cm threshold for eddy detection
              if (sshAnomaly > 0 && sstCenter > (sst.min + sst.max) / 2) {
                eddyType = 'warm-core';
              } else if (sshAnomaly < 0 && sstCenter < (sst.min + sst.max) / 2) {
                eddyType = 'cold-core';
              }
            }
            
            if (Math.abs(sshAnomaly) > 0) allSshAnomalies.push(Math.abs(sshAnomaly));
            if (sshGradient > 0) allSshGradients.push(sshGradient);
          }
        } else {
          // Log missing SSH data
          if (i === 1 && j === 1) {
            console.log('[HotspotRanker] SSH data missing or incomplete', {
              hasSSH: !!ssh,
              hasGrid: !!(ssh?.grid),
              gridLength: ssh?.grid?.length || 0
            });
          }
        }
        
        // Convert grid indices to precise coordinates
        // Use the actual grid resolution for accurate positioning
        const latStep = (bounds.north - bounds.south) / (sst.grid.length - 1);
        const lonStep = (bounds.east - bounds.west) / (sst.grid[i].length - 1);
        
        // Find sub-grid position of maximum gradient using interpolation
        let subGridOffsetLat = 0;
        let subGridOffsetLon = 0;
        
        // If we have strong gradients, interpolate to find the precise maximum location
        if (sstGradient > 0.5 || chloroGradient > 0.1) {
          // Calculate gradient direction to find where the edge actually is
          // The gradient points in the direction of maximum change
          
          // SST gradient components
          const sstDx = ((sst.grid[i][j+1] ?? sstCenter) - (sst.grid[i][j-1] ?? sstCenter)) / 2;
          const sstDy = ((sst.grid[i+1]?.[j] ?? sstCenter) - (sst.grid[i-1]?.[j] ?? sstCenter)) / 2;
          
          // Chlorophyll gradient components  
          const chloroDx = ((chlorophyll.grid[i][j+1] ?? chloroCenter) - (chlorophyll.grid[i][j-1] ?? chloroCenter)) / 2;
          const chloroDy = ((chlorophyll.grid[i+1]?.[j] ?? chloroCenter) - (chlorophyll.grid[i-1]?.[j] ?? chloroCenter)) / 2;
          
          // Combine gradients to find the dominant edge direction
          const totalDx = sstDx * 2 + chloroDx * 20; // Weight chlorophyll more heavily
          const totalDy = sstDy * 2 + chloroDy * 20;
          const gradMag = Math.sqrt(totalDx * totalDx + totalDy * totalDy);
          
          if (gradMag > 0.1) {
            // The edge is perpendicular to the gradient direction
            // Place the hotspot along the edge, not at the grid center
            const edgeOffsetScale = 0.3; // Offset up to 30% of grid cell
            
            // Perpendicular to gradient for edge positioning
            subGridOffsetLat = -(totalDx / gradMag) * edgeOffsetScale * latStep;
            subGridOffsetLon = (totalDy / gradMag) * edgeOffsetScale * lonStep;
            
            // Add small jitter to prevent perfect alignment (max 5% of grid cell)
            subGridOffsetLat += (Math.random() - 0.5) * 0.1 * latStep;
            subGridOffsetLon += (Math.random() - 0.5) * 0.1 * lonStep;
          }
        }
        
        const lat = bounds.south + (i * latStep) + subGridOffsetLat;
        const lon = bounds.west + (j * lonStep) + subGridOffsetLon;
        
        // Store all points with their gradients
        gradientPoints.push({
          lat,
          lon,
          sstGradient,
          chloroGradient,
          currentSpeed,
          currentConvergence,
          sshAnomaly,
          sshGradient,
          eddyType,
          nearbyStructures: [], // Will calculate later for top points only
          score: 0 // Will calculate after we know the percentiles
        });
      }
    }
    
    // Calculate percentiles for dynamic thresholds
    const sstPercentiles = this.calculatePercentiles(allSstGradients);
    const chloroPercentiles = this.calculatePercentiles(allChloroGradients);
    const currentSpeedPercentiles = this.calculatePercentiles(allCurrentSpeeds);
    const currentConvergencePercentiles = this.calculatePercentiles(allCurrentConvergences);
    const sshGradientPercentiles = this.calculatePercentiles(allSshGradients);
    const sshAnomalyPercentiles = this.calculatePercentiles(allSshAnomalies);
    
    console.log(`[HotspotRanker] SST gradient percentiles: 50th=${sstPercentiles.p50.toFixed(2)}, 75th=${sstPercentiles.p75.toFixed(2)}, 90th=${sstPercentiles.p90.toFixed(2)}, 95th=${sstPercentiles.p95.toFixed(2)}, 99th=${sstPercentiles.p99.toFixed(2)}`);
    console.log(`[HotspotRanker] Chloro gradient percentiles: 50th=${chloroPercentiles.p50.toFixed(2)}, 75th=${chloroPercentiles.p75.toFixed(2)}, 90th=${chloroPercentiles.p90.toFixed(2)}, 95th=${chloroPercentiles.p95.toFixed(2)}, 99th=${chloroPercentiles.p99.toFixed(2)}`);
    console.log(`[HotspotRanker] Current speed percentiles: 50th=${currentSpeedPercentiles.p50.toFixed(2)}, 75th=${currentSpeedPercentiles.p75.toFixed(2)}, 90th=${currentSpeedPercentiles.p90.toFixed(2)}, 95th=${currentSpeedPercentiles.p95.toFixed(2)}, 99th=${currentSpeedPercentiles.p99.toFixed(2)}`);
    console.log(`[HotspotRanker] Current convergence percentiles: 50th=${currentConvergencePercentiles.p50.toFixed(2)}, 75th=${currentConvergencePercentiles.p75.toFixed(2)}, 90th=${currentConvergencePercentiles.p90.toFixed(2)}, 95th=${currentConvergencePercentiles.p95.toFixed(2)}, 99th=${currentConvergencePercentiles.p99.toFixed(2)}`);
    console.log(`[HotspotRanker] SSH gradient percentiles: 50th=${sshGradientPercentiles.p50.toFixed(2)}, 75th=${sshGradientPercentiles.p75.toFixed(2)}, 90th=${sshGradientPercentiles.p90.toFixed(2)}, 95th=${sshGradientPercentiles.p95.toFixed(2)}, 99th=${sshGradientPercentiles.p99.toFixed(2)}`);
    console.log(`[HotspotRanker] SSH anomaly percentiles: 50th=${sshAnomalyPercentiles.p50.toFixed(2)}, 75th=${sshAnomalyPercentiles.p75.toFixed(2)}, 90th=${sshAnomalyPercentiles.p90.toFixed(2)}, 95th=${sshAnomalyPercentiles.p95.toFixed(2)}, 99th=${sshAnomalyPercentiles.p99.toFixed(2)}`);
    console.log(`[HotspotRanker] Total gradient points: ${gradientPoints.length}, SST gradients found: ${allSstGradients.length}, Chloro gradients found: ${allChloroGradients.length}, Current speeds found: ${allCurrentSpeeds.length}, SSH gradients found: ${allSshGradients.length}`);
    
    // Handle edge case where no gradients are found
    if (allSstGradients.length === 0 && allChloroGradients.length === 0) {
      console.warn('[HotspotRanker] No gradients found in data - returning empty hotspot list');
      return [];
    }
    
    // Second pass: Calculate scores using percentile-based ranking
    gradientPoints.forEach(point => {
      // Only process points with significant gradients (above 75th percentile for any feature)
      if (point.sstGradient > sstPercentiles.p75 ||
          point.chloroGradient > chloroPercentiles.p75 ||
          point.currentSpeed > currentSpeedPercentiles.p75 ||
          Math.abs(point.currentConvergence) > currentConvergencePercentiles.p75 ||
          point.sshGradient > sshGradientPercentiles.p75 ||
          point.eddyType !== 'none') {
        // Load waypoints if enabled - will be done in a separate pass
        point.nearbyStructures = [];
        
        // Get temperature and chlorophyll at this location
        const temperature = this.getTemperatureAtLocation(data.sst, { lat: point.lat, lon: point.lon }, bounds);
        const chlorophyllConc = this.getChlorophyllAtLocation(data.chlorophyll, { lat: point.lat, lon: point.lon }, bounds);
        
        // Calculate clarity persistence bonus - need to find grid indices
        const gridI = Math.floor((point.lat - bounds.south) / (bounds.north - bounds.south) * data.chlorophyll.grid.length);
        const gridJ = Math.floor((point.lon - bounds.west) / (bounds.east - bounds.west) * data.chlorophyll.grid[0].length);
        const clarityPersistence = this.calculateClarityPersistence(data.chlorophyll, gridI, gridJ);
        
        // Calculate initial score WITHOUT structures (they'll be added in second pass)
        // This prevents filtering out good spots that have structures
        point.score = this.calculateDynamicScore(
          point.sstGradient,
          point.chloroGradient,
          point.currentSpeed,
          point.currentConvergence,
          point.sshGradient,
          point.sshAnomaly,
          point.eddyType,
          0, // Don't count structures yet
          temperature,
          chlorophyllConc,
          sstPercentiles,
          chloroPercentiles,
          currentSpeedPercentiles,
          currentConvergencePercentiles,
          sshGradientPercentiles,
          sshAnomalyPercentiles,
          clarityPersistence
        );
      }
    });
    
    // Second pass: Load waypoints for scored points if enabled
    // DEFAULT TO TRUE if options not provided - structures are important!
    const shouldUseWaypoints = options?.useWaypoints !== false;

    if (shouldUseWaypoints) {
      console.log('[HotspotRanker] Loading waypoints for top points');
      const waypointType = options?.waypointType || 'public';
      console.log('[HotspotRanker] Using waypoint type:', waypointType);

      // Pre-load all personal waypoints if needed (cache them for this ranking session)
      if ((waypointType === 'personal' || waypointType === 'both') && options?.userId) {
        await this.loadPersonalWaypoints(options.userId);
      }

      const topPoints = gradientPoints.filter(p => p.score > 0).sort((a, b) => b.score - a.score).slice(0, 100);
      console.log(`[HotspotRanker] Checking structures for ${topPoints.length} top points`);

      for (const point of topPoints) {
        point.nearbyStructures = await this.getNearbyWaypoints(
          point.lat,
          point.lon,
          waypointType,
          options?.userId
        );

        if (point.nearbyStructures && point.nearbyStructures.length > 0) {
          console.log(`[HotspotRanker] Found ${point.nearbyStructures.length} structures near ${point.lat.toFixed(4)}, ${point.lon.toFixed(4)}`);
        }
        // Recalculate score with structure information
        point.score = this.calculateDynamicScore(
          point.sstGradient,
          point.chloroGradient,
          point.currentSpeed,
          point.currentConvergence,
          point.sshGradient,
          point.sshAnomaly,
          point.eddyType,
          point.nearbyStructures?.length || 0,
          this.getTemperatureAtLocation(data.sst, { lat: point.lat, lon: point.lon }, bounds),
          this.getChlorophyllAtLocation(data.chlorophyll, { lat: point.lat, lon: point.lon }, bounds),
          sstPercentiles,
          chloroPercentiles,
          currentSpeedPercentiles,
          currentConvergencePercentiles,
          sshGradientPercentiles,
          sshAnomalyPercentiles
        );
      }
    } else {
      console.log('[HotspotRanker] Waypoints disabled - not loading structures');
    }

    // Filter out zero-score points and sort by score
    const scoredPoints = gradientPoints.filter(p => p.score > 0);
    
    // Calculate distance from port if provided
    if (homePort) {
      scoredPoints.forEach(point => {
        point.distanceFromPort = this.calculateDistanceNM(homePort, { lat: point.lat, lon: point.lon });
        // Calculate practicality score based on distance (optimal 10-50nm, max 100nm)
        if (point.distanceFromPort <= 10) {
          point.practicalityScore = 0.5; // Too close, likely less productive
        } else if (point.distanceFromPort <= 30) {
          point.practicalityScore = 1.0; // Optimal range for day trips
        } else if (point.distanceFromPort <= 50) {
          point.practicalityScore = 0.9; // Good for longer day trips
        } else if (point.distanceFromPort <= 75) {
          point.practicalityScore = 0.7; // Requires early start or overnight
        } else if (point.distanceFromPort <= 100) {
          point.practicalityScore = 0.5; // Multi-day trip territory
        } else {
          point.practicalityScore = 0.2; // Too far for most recreational fishing
        }
      });
    }
    
    // Sort by combined score (fishing potential + practicality)
    scoredPoints.sort((a, b) => {
      const scoreA = a.score * (a.practicalityScore || 1);
      const scoreB = b.score * (b.practicalityScore || 1);
      return scoreB - scoreA;
    });
    
    console.log(`[HotspotRanker] Points with score > 0: ${scoredPoints.length}, Top score: ${scoredPoints[0]?.score.toFixed(2) || 'N/A'}`);
    
    // Dynamic clustering based on score similarity and distance
    const clusteredHotspots = this.dynamicCluster(scoredPoints, homePort, options);
    
    // Convert to FishingHotspot format
    const hotspots: FishingHotspot[] = clusteredHotspots.map((point, index) => {
      const waterTemp = this.getTemperatureAtLocation(data.sst, { lat: point.lat, lon: point.lon }, bounds);
      const chloroConc = this.getChlorophyllAtLocation(data.chlorophyll, { lat: point.lat, lon: point.lon }, bounds);
      const { speed: currentSpeed, direction: currentDirection } = this.getCurrentDataAtLocation(
        data.currents, 
        { lat: point.lat, lon: point.lon }, 
        bounds
      );
      
      const reasons = this.generateReasons(point, waterTemp, chloroConc, sstPercentiles, chloroPercentiles, currentSpeedPercentiles, currentConvergencePercentiles, sshGradientPercentiles, sshAnomalyPercentiles);
      
      // Add distance information to reasons if available
      if (point.distanceFromPort !== undefined) {
        const distanceReason = `${point.distanceFromPort.toFixed(1)}nm from port - ${
          point.distanceFromPort <= 30 ? 'ideal for day trips' :
          point.distanceFromPort <= 60 ? 'perfect for extended trips' :
          point.distanceFromPort <= 100 ? 'overnight trip recommended' :
          'multi-day expedition'
        }`;
        reasons.unshift(distanceReason);
      }
      
      // Build water conditions with gradients and special conditions
      const waterConditions = {
        temperature: waterTemp,
        chlorophyll: chloroConc,
        depth: undefined,
        clarity: undefined,
        salinity: undefined,
        currentSpeed,
        currentDirection,
        gradients: [] as string[]
      };
      
      // Add gradient information - show all available data
      if (point.sstGradient > 0.5) {
        waterConditions.gradients.push(
          `SST gradient: ${point.sstGradient.toFixed(1)}°C/100km`
        );
      }
      
      if (point.chloroGradient > 0.1) {
        waterConditions.gradients.push(
          `Chlorophyll gradient: ${point.chloroGradient.toFixed(2)} mg/m³/100km`
        );
      }
      
      // Show current convergence even for small values
      if (point.currentConvergence !== 0 && currentSpeed > 0) {
        if (point.currentConvergence < 0) {
          waterConditions.gradients.push(
            `Current convergence: ${Math.abs(point.currentConvergence).toFixed(3)} 1/s`
          );
        } else {
          waterConditions.gradients.push(
            `Current divergence: ${point.currentConvergence.toFixed(3)} 1/s`
          );
        }
      }
      
      // Show SSH gradient for any non-zero value
      if (point.sshGradient !== 0) {
        waterConditions.gradients.push(
          `SSH gradient: ${point.sshGradient.toFixed(3)} m/100km`
        );
      }
      
      // Remove special conditions - they'll be included in reasons instead
      
      const hotspot: FishingHotspot = {
        location: { lat: point.lat, lon: point.lon },
        confidence: Math.min(0.95, 0.4 + (point.score / 20)),
        score: point.score,
        reasons,
        targetSpecies: this.predictSpecies(waterTemp, []),
        optimalTime: {
          start: '06:00',
          end: '10:00',
          tidePhase: 'incoming'
        },
        waterConditions,
        nearbyFeatures: [],
        nearbyStructures: point.nearbyStructures || []
      };

      // Debug log for structures
      if (point.nearbyStructures && point.nearbyStructures.length > 0) {
        console.log(`[HotspotRanker] Hotspot ${index + 1} has ${point.nearbyStructures.length} structures:`,
          point.nearbyStructures.map(s => s.name || s.description));
      }
      
      return hotspot;
    });
    
    console.log(`[HotspotRanker] Identified ${hotspots.length} ranked hotspots`);

    // Log how many hotspots have structures
    const hotspotsWithStructures = hotspots.filter(h => h.nearbyStructures && h.nearbyStructures.length > 0);
    console.log(`[HotspotRanker] ${hotspotsWithStructures.length} of ${hotspots.length} hotspots have nearby structures`);

    if (hotspotsWithStructures.length > 0) {
      console.log('[HotspotRanker] First hotspot with structures:', {
        location: hotspotsWithStructures[0].location,
        structures: hotspotsWithStructures[0].nearbyStructures?.map(s => s.name || s.description)
      });
    }

    return hotspots.slice(0, 30); // Return top 30
  }
  
  private calculatePercentiles(values: number[]): { p50: number; p75: number; p90: number; p95: number; p99: number } {
    if (values.length === 0) {
      return { p50: 0, p75: 0, p90: 0, p95: 0, p99: 0 };
    }
    
    const sorted = [...values].sort((a, b) => a - b);
    const getPercentile = (p: number) => {
      const index = Math.ceil((p / 100) * sorted.length) - 1;
      return sorted[Math.max(0, Math.min(index, sorted.length - 1))];
    };
    
    return {
      p50: getPercentile(50),
      p75: getPercentile(75),
      p90: getPercentile(90),
      p95: getPercentile(95),
      p99: getPercentile(99)
    };
  }
  
  private calculateClarityPersistence(chlorophyll: any, centerI: number, centerJ: number): number {
    // Check surrounding cells for consistent clear water
    // Award 0-3 bonus points based on how many neighboring cells also have clear water
    let clearCells = 0;
    let totalCells = 0;
    const checkRadius = 2; // Check cells within 2 grid points
    
    const centerValue = chlorophyll.grid[centerI]?.[centerJ];
    if (centerValue === null || centerValue === undefined || centerValue > 0.35) {
      return 0; // No bonus if center cell isn't clear
    }
    
    for (let di = -checkRadius; di <= checkRadius; di++) {
      for (let dj = -checkRadius; dj <= checkRadius; dj++) {
        if (di === 0 && dj === 0) continue; // Skip center cell
        
        const i = centerI + di;
        const j = centerJ + dj;
        
        if (i >= 0 && i < chlorophyll.grid.length && 
            j >= 0 && j < chlorophyll.grid[i].length) {
          const value = chlorophyll.grid[i][j];
          if (value !== null && value !== undefined && value >= 0 && value <= 1.2) {
            totalCells++;
            if (value < 0.35) { // Clear water threshold
              clearCells++;
            }
          }
        }
      }
    }
    
    if (totalCells === 0) return 0;
    
    const clearRatio = clearCells / totalCells;
    
    // Award bonus based on percentage of clear neighboring cells
    if (clearRatio >= 0.8) {
      return 3; // 80%+ clear neighbors - expansive clear water area
    } else if (clearRatio >= 0.6) {
      return 2; // 60%+ clear neighbors - good clear water area
    } else if (clearRatio >= 0.4) {
      return 1; // 40%+ clear neighbors - moderate clear water area
    }
    
    return 0;
  }
  
  private calculateDynamicScore(
    sstGradient: number,
    chloroGradient: number,
    currentSpeed: number,
    currentConvergence: number,
    sshGradient: number,
    sshAnomaly: number,
    eddyType: 'warm-core' | 'cold-core' | 'none',
    structureCount: number,
    temperature: number,
    chlorophyll: number,
    sstPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    chloroPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    currentSpeedPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    currentConvergencePercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    sshGradientPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    sshAnomalyPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    clarityPersistence?: number
  ): number {
    let score = 0;
    
    // SST gradient score (0-10 points based on percentile) - DOUBLED WEIGHT for temperature breaks
    if (sstGradient >= sstPercentiles.p99) {
      score += 10; // Top 1% - exceptional temperature break
    } else if (sstGradient >= sstPercentiles.p95) {
      score += 8; // Top 5% - strong temperature break
    } else if (sstGradient >= sstPercentiles.p90) {
      score += 6; // Top 10% - significant temperature break
    } else if (sstGradient >= sstPercentiles.p75) {
      score += 4; // Top 25% - notable temperature break
    } else if (sstGradient >= sstPercentiles.p50) {
      score += 2; // Above median - light temperature break
    }
    
    // Chlorophyll gradient score (0-8 points based on percentile) - QUADRUPLED WEIGHT for color breaks
    if (chloroGradient >= chloroPercentiles.p99) {
      score += 8; // Top 1% - major water color change
    } else if (chloroGradient >= chloroPercentiles.p95) {
      score += 6; // Top 5% - strong blue/green water edge
    } else if (chloroGradient >= chloroPercentiles.p90) {
      score += 4; // Top 10% - significant color change
    } else if (chloroGradient >= chloroPercentiles.p75) {
      score += 2; // Top 25% - notable color transition
    } else if (chloroGradient >= chloroPercentiles.p50) {
      score += 1; // Above median - light color change
    }
    
    // Current speed score (0-2 points based on percentile) - REDUCED WEIGHT
    // Minor factor compared to temperature/color breaks
    if (currentSpeed >= currentSpeedPercentiles.p50 && currentSpeed <= currentSpeedPercentiles.p90) {
      score += 2; // Optimal current speed range
    } else if (currentSpeed > 0) {
      score += 1; // Any current present
    }
    
    // Current convergence score (0-2 points) - REDUCED WEIGHT
    // Minor factor compared to temperature/color breaks
    const absConvergence = Math.abs(currentConvergence);
    if (currentConvergence < 0) { // Negative values indicate convergence
      if (absConvergence >= currentConvergencePercentiles.p95) {
        score += 2; // Strong convergence zone
      } else if (absConvergence >= currentConvergencePercentiles.p75) {
        score += 1; // Moderate convergence
      }
    }
    
    // SSH gradient and eddy scoring (0-3 points total) - MINIMAL WEIGHT
    // Secondary factor compared to temperature/color breaks
    if (eddyType === 'warm-core') {
      score += 3; // Warm-core eddies can concentrate pelagics
    } else if (sshGradient >= sshGradientPercentiles.p90) {
      score += 2; // Strong SSH gradient indicates eddy edge
    } else if (eddyType === 'cold-core') {
      score += 1; // Cold-core eddies have some potential
    }
    
    // Structure proximity score (0-10 points) - MAJOR WEIGHT for waypoints
    // Structures are proven fish magnets and should heavily influence rankings
    if (structureCount > 0) {
      if (structureCount >= 3) {
        score += 10; // Multiple structures nearby - PRIME fishing location
      } else if (structureCount === 2) {
        score += 7; // Two structures - excellent spot
      } else if (structureCount === 1) {
        score += 4; // One structure - good spot
      }
    }
    
    // Temperature preference bonus (0-2 points)
    // Optimal fishing temp 24-28°C
    if (temperature >= 24 && temperature <= 28) {
      score += 2;
    } else if (temperature >= 22 && temperature <= 30) {
      score += 1;
    }
    
    // Water clarity bonus (0-3 points) - clear water preference for pelagics
    if (chlorophyll >= 0.1 && chlorophyll < 0.3) {
      score += 3; // Optimal clarity - perfect blue water for pelagic species
    } else if (chlorophyll >= 0.3 && chlorophyll < 0.5) {
      score += 2; // Good clarity - productive yet clear
    } else if ((chlorophyll >= 0 && chlorophyll < 0.1) || (chlorophyll >= 0.5 && chlorophyll < 1.0)) {
      score += 1; // Very clear or slightly green - still fishable
    }
    
    // Clarity persistence bonus (0-3 points) - reward expansive clear water areas
    if (clarityPersistence !== undefined) {
      score += clarityPersistence;
    }
    
    return score;
  }
  
  
  private calculateDistance(loc1: { lat: number; lon: number }, loc2: { lat: number; lon: number }): number {
    // Use proper distance calculation
    return this.calculateDistanceNM(loc1, loc2);
  }
  
  private calculateDistanceNM(loc1: { lat: number; lon: number }, loc2: { lat: number; lon: number }): number {
    // Haversine formula for nautical miles
    const R = 3440.065; // Earth's radius in nautical miles
    const lat1Rad = loc1.lat * Math.PI / 180;
    const lat2Rad = loc2.lat * Math.PI / 180;
    const deltaLat = (loc2.lat - loc1.lat) * Math.PI / 180;
    const deltaLon = (loc2.lon - loc1.lon) * Math.PI / 180;
    
    const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
              Math.cos(lat1Rad) * Math.cos(lat2Rad) *
              Math.sin(deltaLon/2) * Math.sin(deltaLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    
    return R * c;
  }
  
  private dynamicCluster(points: GradientPoint[], homePort?: { lat: number; lon: number }, options?: any): GradientPoint[] {
    const clustered: GradientPoint[] = [];
    const used = new Set<number>();
    
    for (let i = 0; i < points.length; i++) {
      if (used.has(i)) continue;
      
      const cluster: GradientPoint[] = [points[i]];
      used.add(i);
      
      // Dynamic clustering radius based on score similarity
      const baseScore = points[i].score;
      const clusterRadiusNM = baseScore > 20 ? 9 : 6; // 9nm or 6nm radius in nautical miles
      
      // Find similar-scoring points nearby
      for (let j = i + 1; j < points.length; j++) {
        if (used.has(j)) continue;
        
        const distance = this.calculateDistance(
          { lat: points[i].lat, lon: points[i].lon },
          { lat: points[j].lat, lon: points[j].lon }
        );
        
        const scoreDiff = Math.abs(points[j].score - baseScore) / baseScore;
        
        // Cluster if nearby AND similar score (within 20%)
        if (distance < clusterRadiusNM && scoreDiff < 0.2) {
          cluster.push(points[j]);
          used.add(j);
        }
      }
      
      // Use the highest scoring point in the cluster
      const bestPoint = cluster.reduce((best, current) => {
        const bestCombined = best.score * (best.practicalityScore || 1);
        const currentCombined = current.score * (current.practicalityScore || 1);
        return currentCombined > bestCombined ? current : best;
      });
      
      // Merge nearby structures from all cluster points
      if (options?.useWaypoints && cluster.length > 1) {
        const allStructures = new Map<string, Structure>();
        for (const p of cluster) {
          if (p.nearbyStructures) {
            for (const s of p.nearbyStructures) {
              const key = `${s.latitude},${s.longitude}`;
              if (!allStructures.has(key)) {
                allStructures.set(key, s);
              }
            }
          }
        }
        bestPoint.nearbyStructures = Array.from(allStructures.values());
      } else if (!options?.useWaypoints) {
        bestPoint.nearbyStructures = [];
      }
      clustered.push(bestPoint);
    }
    
    return clustered;
  }
  
  private generateReasons(
    point: GradientPoint, 
    temperature: number,
    chlorophyll: number,
    sstPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    chloroPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    currentSpeedPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    currentConvergencePercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    sshGradientPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number },
    sshAnomalyPercentiles: { p50: number; p75: number; p90: number; p95: number; p99: number }
  ): string[] {
    const reasons: string[] = [];
    
    // Temperature gradient - describe based on percentile ranking
    if (point.sstGradient > 0) {
      let descriptor = "Temperature gradient";
      if (point.sstGradient >= sstPercentiles.p99) {
        descriptor = "Exceptional temperature gradient (top 1%)";
      } else if (point.sstGradient >= sstPercentiles.p95) {
        descriptor = "Strong temperature gradient (top 5%)";
      } else if (point.sstGradient >= sstPercentiles.p90) {
        descriptor = "Significant temperature gradient (top 10%)";
      } else if (point.sstGradient >= sstPercentiles.p75) {
        descriptor = "Notable temperature gradient (top 25%)";
      }
      reasons.push(`${descriptor} - ${UnitConversions.formatTemperatureGradient(point.sstGradient)} change creates prime fishing edge`);
    }
    
    // Water clarity conditions - emphasize clear water preference
    if (chlorophyll >= 0.05 && chlorophyll < 0.15) {
      reasons.push(`Ultra-clear blue water (${UnitConversions.formatChlorophyllConcentration(chlorophyll)}) - optimal visibility for pelagic species`);
    } else if (chlorophyll >= 0.15 && chlorophyll < 0.25) {
      reasons.push(`Very clear water (${UnitConversions.formatChlorophyllConcentration(chlorophyll)}) - excellent fishing conditions`);
    } else if (chlorophyll >= 0.25 && chlorophyll < 0.35) {
      reasons.push(`Clear water (${UnitConversions.formatChlorophyllConcentration(chlorophyll)}) - good visibility for sight fishing`);
    } else if (chlorophyll >= 0.35 && chlorophyll < 0.50) {
      reasons.push(`Moderately clear water (${UnitConversions.formatChlorophyllConcentration(chlorophyll)}) - acceptable conditions`);
    } else if (chlorophyll >= 0 && chlorophyll < 0.05) {
      reasons.push(`Extremely clear water (${UnitConversions.formatChlorophyllConcentration(chlorophyll)}) - gin-clear conditions`);
    }
    
    // Chlorophyll gradient - only mention if significant
    if (point.chloroGradient >= chloroPercentiles.p95) {
      reasons.push(`Water color change detected - ${UnitConversions.formatChlorophyllGradient(point.chloroGradient)} gradient marks transition zone`);
    }
    
    // Current speed - describe based on percentile ranking
    if (point.currentSpeed > 0) {
      let descriptor = "Current present";
      if (point.currentSpeed >= currentSpeedPercentiles.p50 && point.currentSpeed <= currentSpeedPercentiles.p90) {
        descriptor = "Optimal current speed";
        reasons.push(`${descriptor} - ${UnitConversions.formatCurrentSpeed(point.currentSpeed)} creates ideal fishing conditions`);
      } else if (point.currentSpeed >= currentSpeedPercentiles.p95) {
        descriptor = "Strong current";
        reasons.push(`${descriptor} - ${UnitConversions.formatCurrentSpeed(point.currentSpeed)} may concentrate bait in eddies`);
      } else if (point.currentSpeed >= currentSpeedPercentiles.p75) {
        descriptor = "Good current flow";
        reasons.push(`${descriptor} - ${UnitConversions.formatCurrentSpeed(point.currentSpeed)} helps position baitfish`);
      } else if (point.currentSpeed >= currentSpeedPercentiles.p50) {
        descriptor = "Light current";
        reasons.push(`${descriptor} - ${UnitConversions.formatCurrentSpeed(point.currentSpeed)} provides water movement`);
      } else {
        // Show even minimal current
        reasons.push(`Minimal current - ${UnitConversions.formatCurrentSpeed(point.currentSpeed)}`);
      }
    }
    
    // Current convergence - highlight convergence zones
    if (point.currentConvergence < 0) { // Negative values indicate convergence
      const absConvergence = Math.abs(point.currentConvergence);
      let descriptor = "Current convergence";
      if (absConvergence >= currentConvergencePercentiles.p95) {
        descriptor = "Major convergence zone";
      } else if (absConvergence >= currentConvergencePercentiles.p90) {
        descriptor = "Strong convergence zone";
      } else if (absConvergence >= currentConvergencePercentiles.p75) {
        descriptor = "Good convergence zone";
      }
      reasons.push(`${descriptor} - concentrates floating debris and baitfish`);
    }
    
    // Eddy features - Major attractors for pelagic species
    if (point.eddyType === 'warm-core') {
      let descriptor = "Warm-core eddy";
      if (Math.abs(point.sshAnomaly) >= sshAnomalyPercentiles.p95) {
        descriptor = "Major warm-core eddy (top 5%)";
      } else if (Math.abs(point.sshAnomaly) >= sshAnomalyPercentiles.p90) {
        descriptor = "Strong warm-core eddy (top 10%)";
      } else if (Math.abs(point.sshAnomaly) >= sshAnomalyPercentiles.p75) {
        descriptor = "Significant warm-core eddy";
      }
      reasons.push(`${descriptor} - SSH anomaly: ${(point.sshAnomaly * 100).toFixed(1)}cm - prime pelagic habitat with concentrated prey`);
    } else if (point.eddyType === 'cold-core') {
      let descriptor = "Cold-core eddy";
      if (Math.abs(point.sshAnomaly) >= sshAnomalyPercentiles.p95) {
        descriptor = "Major cold-core eddy (top 5%)";
      } else if (Math.abs(point.sshAnomaly) >= sshAnomalyPercentiles.p90) {
        descriptor = "Strong cold-core eddy (top 10%)";
      } else if (Math.abs(point.sshAnomaly) >= sshAnomalyPercentiles.p75) {
        descriptor = "Significant cold-core eddy";
      }
      reasons.push(`${descriptor} - SSH anomaly: ${(Math.abs(point.sshAnomaly) * 100).toFixed(1)}cm - upwelling brings nutrients to surface`);
    }
    
    // SSH gradient (eddy edges)
    if (point.sshGradient > 0) {
      let descriptor = "SSH feature";
      if (point.sshGradient >= sshGradientPercentiles.p95) {
        descriptor = "Major SSH gradient (top 5%)";
      } else if (point.sshGradient >= sshGradientPercentiles.p90) {
        descriptor = "Strong SSH gradient (top 10%)";
      } else if (point.sshGradient >= sshGradientPercentiles.p75) {
        descriptor = "Notable SSH gradient (top 25%)";
      } else if (point.sshGradient >= sshGradientPercentiles.p50) {
        descriptor = "Moderate SSH gradient";
      } else {
        descriptor = "Slight SSH gradient";
      }
      reasons.push(`${descriptor} - ${(point.sshGradient * 5280 / 1609.344).toFixed(3)} ft/mi ${point.eddyType === 'none' ? 'indicates water mass boundary' : ''}`);
    }
    
    // Structure proximity - PRIORITIZE THIS IN REASONS
    if (point.nearbyStructures && point.nearbyStructures.length > 0) {
      // Move structure reasons to the TOP of the list since they're so important
      const structureReasons: string[] = [];

      if (point.nearbyStructures.length >= 3) {
        const names = point.nearbyStructures.slice(0, 3)
          .map(s => s.name || s.description || 'structure')
          .filter(n => n && n !== 'Structure')
          .join(', ');
        const moreCount = point.nearbyStructures.length - 3;
        structureReasons.push(`🎯 PRIME LOCATION: Near ${point.nearbyStructures.length} structures (${names}${moreCount > 0 ? ` +${moreCount} more` : ''}) - proven fish magnets`);
      } else if (point.nearbyStructures.length === 2) {
        const names = point.nearbyStructures
          .map(s => s.name || s.description || 'structure')
          .filter(n => n && n !== 'Structure')
          .join(' and ');
        structureReasons.push(`🎯 Between ${names} - excellent structure positioning`);
      } else {
        const name = point.nearbyStructures[0].name || point.nearbyStructures[0].description || 'structure';
        if (name && name !== 'Structure') {
          structureReasons.push(`🎯 Near ${name} - structure holds fish`);
        }
      }

      // Add structure reasons at the BEGINNING
      reasons.unshift(...structureReasons);
    }
    
    // Water temperature conditions
    if (temperature >= 24 && temperature <= 28) {
      reasons.push(`Ideal water temperature ${UnitConversions.formatTemperature(temperature)} for pelagic species`);
    }
    
    return reasons;
  }
  
  private predictSpecies(temperature: number, structures?: Structure[]): string[] {
    const species = new Set<string>();
    
    // Temperature-based species
    if (temperature >= 26 && temperature <= 30) {
      species.add('Mahi Mahi');
      species.add('Wahoo');
      species.add('Blackfin Tuna');
    }
    if (temperature >= 24 && temperature <= 28) {
      species.add('Yellowfin Tuna');
      species.add('Blue Marlin');
    }
    if (temperature >= 22 && temperature <= 26) {
      species.add('Sailfish');
      species.add('King Mackerel');
    }
    if (temperature >= 20 && temperature <= 24) {
      species.add('Cobia');
      species.add('Amberjack');
    }
    
    // Structure-based species
    if (structures && structures.length > 0) {
      // Reef and structure species
      species.add('Snapper');
      species.add('Grouper');
      species.add('Jack Crevalle');

      // Check for specific structure types
      const hasRig = structures.some(s => s.description?.toLowerCase().includes('rig') ||
                                          s.description?.toLowerCase().includes('platform'));
      const hasWreck = structures.some(s => s.description?.toLowerCase().includes('wreck'));
      const hasReef = structures.some(s => s.description?.toLowerCase().includes('reef'));

      if (hasRig) {
        species.add('Triple Tail');
        species.add('Cobia');
      }
      if (hasWreck) {
        species.add('Barracuda');
        species.add('Amberjack');
      }
      if (hasReef) {
        species.add('Red Snapper');
        species.add('Vermilion Snapper');
      }
    }
    
    return Array.from(species);
  }
  
  private getTemperatureAtLocation(
    sst: any,
    location: { lat: number; lon: number },
    bounds: BoundingBox
  ): number {
    const i = Math.floor((location.lat - bounds.south) / (bounds.north - bounds.south) * sst.grid.length);
    const j = Math.floor((location.lon - bounds.west) / (bounds.east - bounds.west) * sst.grid[0].length);
    
    if (i >= 0 && i < sst.grid.length && j >= 0 && j < sst.grid[0].length) {
      return sst.grid[i][j] || 25;
    }
    
    return 25; // Default temperature
  }
  
  private getChlorophyllAtLocation(
    chlorophyll: any,
    location: { lat: number; lon: number },
    bounds: BoundingBox
  ): number {
    const i = Math.floor((location.lat - bounds.south) / (bounds.north - bounds.south) * chlorophyll.grid.length);
    const j = Math.floor((location.lon - bounds.west) / (bounds.east - bounds.west) * chlorophyll.grid[0].length);
    
    if (i >= 0 && i < chlorophyll.grid.length && j >= 0 && j < chlorophyll.grid[0].length) {
      return chlorophyll.grid[i][j] || 1.0;
    }
    
    return 1.0; // Default chlorophyll
  }
  
  private async loadPersonalWaypoints(userId: string): Promise<void> {
    // Only reload if we don't have cached waypoints for this user
    if (this.personalWaypointsCacheUserId !== userId || !this.personalWaypointsCache) {
      console.log(`[HotspotRanker] Loading all personal waypoints for user ${userId} once`);

      try {
        const allWaypoints = await PrivateWaypointsService.getAllWaypointsForUser(userId);

        // Convert to Structure format and cache
        this.personalWaypointsCache = allWaypoints.map((w: any) => ({
          latitude: w.coordinates.latitude,
          longitude: w.coordinates.longitude,
          name: w.name,
          description: w.category || 'Personal Waypoint'
        }));

        this.personalWaypointsCacheUserId = userId;
        console.log(`[HotspotRanker] Cached ${this.personalWaypointsCache.length} personal waypoints for efficient searching`);
      } catch (error) {
        console.error('[HotspotRanker] Error loading personal waypoints:', error);
        this.personalWaypointsCache = [];
        this.personalWaypointsCacheUserId = userId;
      }
    }
  }

  private async getNearbyWaypoints(
    lat: number,
    lon: number,
    waypointType: 'public' | 'personal' | 'both',
    userId?: string
  ): Promise<Structure[]> {
    const structures: Structure[] = [];
    const searchRadiusNM = 5; // Look for waypoints within 5 nautical miles

    try {
      // Get public waypoints
      if (waypointType === 'public' || waypointType === 'both') {
        // Search fishing spots
        const fishingSpots = await waypointService.getNearbyWaypoints(
          lat, lon, searchRadiusNM, 10, 'fishing'
        );
        structures.push(...fishingSpots.map(w => ({
          latitude: w.latitude,
          longitude: w.longitude,
          name: w.name,
          description: w.description || 'Fishing Spot'
        })));

        // Search wrecks
        const wrecks = await waypointService.getNearbyWaypoints(
          lat, lon, searchRadiusNM, 5, 'wrecks'
        );
        structures.push(...wrecks.map(w => ({
          latitude: w.latitude,
          longitude: w.longitude,
          name: w.name,
          description: w.description || 'Wreck'
        })));

        // Search oil/gas platforms
        const platforms = await waypointService.getNearbyWaypoints(
          lat, lon, searchRadiusNM, 5, 'oilgas'
        );
        structures.push(...platforms.map(w => ({
          latitude: w.latitude,
          longitude: w.longitude,
          name: w.name,
          description: w.description || 'Platform'
        })));
      }

      // Get personal waypoints from cache
      if ((waypointType === 'personal' || waypointType === 'both') && userId && this.personalWaypointsCache) {
        // Filter cached waypoints by distance
        const nearbyPersonal = this.personalWaypointsCache.filter(w => {
          const distance = this.calculateDistanceNM(
            { lat, lon },
            { lat: w.latitude, lon: w.longitude }
          );
          return distance <= searchRadiusNM;
        });

        structures.push(...nearbyPersonal);
      }
    } catch (error) {
      console.error('[HotspotRanker] Error loading waypoints:', error);
    }

    // Sort by distance and limit to closest 5
    const withDistance = structures.map(s => ({
      ...s,
      distance: this.calculateDistanceNM({ lat, lon }, { lat: s.latitude, lon: s.longitude })
    }));

    return withDistance
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5)
      .map(({ distance, ...s }) => s);
  }

  private getCurrentDataAtLocation(
    currents: CurrentData | null,
    location: { lat: number; lon: number },
    bounds: BoundingBox
  ): { speed: number; direction: number } {
    if (!currents || !currents.speedGrid || !currents.directionGrid) {
      console.log('[HotspotRanker] No current data available');
      return { speed: 0, direction: 0 };
    }
    
    const i = Math.floor((location.lat - bounds.south) / (bounds.north - bounds.south) * currents.speedGrid.length);
    const j = Math.floor((location.lon - bounds.west) / (bounds.east - bounds.west) * currents.speedGrid[0].length);
    
    // Debug logging
    console.log(`[HotspotRanker] Getting current at ${location.lat.toFixed(4)}, ${location.lon.toFixed(4)} -> grid[${i}][${j}]`);
    
    if (i >= 0 && i < currents.speedGrid.length && j >= 0 && j < currents.speedGrid[0].length) {
      const speed = currents.speedGrid[i][j];
      const direction = currents.directionGrid[i][j];
      
      // Check if the value is null (no data at this location)
      if (speed === null || speed === undefined) {
        console.log(`[HotspotRanker] No current data at grid[${i}][${j}], trying interpolation`);
        
        // Try to interpolate from neighboring cells
        const neighbors = [];
        for (let di = -1; di <= 1; di++) {
          for (let dj = -1; dj <= 1; dj++) {
            if (di === 0 && dj === 0) continue;
            const ni = i + di;
            const nj = j + dj;
            if (ni >= 0 && ni < currents.speedGrid.length && 
                nj >= 0 && nj < currents.speedGrid[0].length) {
              const neighborSpeed = currents.speedGrid[ni][nj];
              const neighborDir = currents.directionGrid[ni][nj];
              if (neighborSpeed !== null && neighborSpeed !== undefined) {
                neighbors.push({ speed: neighborSpeed, direction: neighborDir || 0 });
              }
            }
          }
        }
        
        if (neighbors.length > 0) {
          // Average the neighboring values
          const avgSpeed = neighbors.reduce((sum, n) => sum + n.speed, 0) / neighbors.length;
          const avgDirection = neighbors.reduce((sum, n) => sum + n.direction, 0) / neighbors.length;
          console.log(`[HotspotRanker] Interpolated from ${neighbors.length} neighbors: ${avgSpeed.toFixed(2)} m/s @ ${avgDirection.toFixed(0)}°`);
          return { speed: avgSpeed, direction: avgDirection };
        }
        
        return { speed: 0, direction: 0 };
      }
      
      console.log(`[HotspotRanker] Found current: ${speed} m/s @ ${direction}°`);
      return { speed, direction: direction || 0 };
    }
    
    console.log(`[HotspotRanker] Grid indices out of bounds: [${i}][${j}]`);
    return { speed: 0, direction: 0 };
  }
  
}

export const hotspotRanker = HotspotRanker.getInstance();